import { useId, useRef, useState } from "react";
import { Calendar } from "./Calendar";
import { Popover } from "./Popover";

interface DateFieldProps {
  value: string;
  onChange: (day: string) => void;
  label: string;
  min?: string;
  max?: string;
  // Shades the span to the other end of a from/to pair.
  rangeFrom?: string;
  required?: boolean;
}

function pretty(day: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!match) return "";
  return new Intl.DateTimeFormat("en", { weekday: "short", day: "numeric", month: "short", year: "numeric" })
    .format(new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
}

// A day is chosen from a calendar, not typed into a box whose popup the page
// cannot style. The value is kept as an ISO day so callers are unchanged.
export function DateField({ value, onChange, label, min, max, rangeFrom, required = false }: DateFieldProps) {
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const id = useId();

  return (
    <div className="field date-field">
      <span className="field-label" id={`${id}-label`}>{label}</span>
      <button
        type="button" className={`date-trigger ${open ? "open" : ""}`} ref={trigger}
        aria-labelledby={`${id}-label`} aria-haspopup="dialog" aria-expanded={open}
        aria-required={required || undefined} onClick={() => setOpen((value) => !value)}
      >
        <span className={value ? "date-value" : "date-value placeholder"}>{value ? pretty(value) : "Choose a day"}</span>
        <svg className="date-icon" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M7 3v3M17 3v3M4 9h16M5 6h14v14H5z" />
        </svg>
      </button>
      <Popover
        anchorRef={trigger} open={open} onDismiss={() => setOpen(false)}
        className="calendar-popover" role="dialog" labelledBy={`${id}-label`}
        onKeyDown={(event) => {
          if (event.key !== "Escape") return;
          event.preventDefault();
          setOpen(false);
          trigger.current?.focus();
        }}
      >
        <Calendar
          value={value} min={min} max={max} rangeFrom={rangeFrom} label={label}
          onChange={(day) => { onChange(day); setOpen(false); trigger.current?.focus(); }}
        />
      </Popover>
    </div>
  );
}
